import React, { useState } from "react";
import { FiPlus } from "react-icons/fi";
import { TabHeader } from "components/TabHeader";
import { Button } from "newui/components/Button";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import { rolesPermissionsSteps } from "guides/features/Settings";
import { useRoles } from "newui/features/RolesPermissions/hooks/useRoles";
import { RolesList } from "./RolesList";
import { PermissionMatrixEditor } from "./PermissionMatrixEditor";
import { CreateRoleModal } from "./CreateRoleModal";

const ROLES_PERMISSIONS_INSTRUCTIONS: React.ReactNode[] = [
	"Pick a role on the left to focus it — the matrix collapses to just that role.",
	"System roles are read-only. Clone one, or create a new role, to customise permissions.",
	"Click a cell to cycle its access level (Full → View → Comment → No access), then Save changes.",
	"Cell colours match the legend so you can scan a role's access at a glance.",
];

export interface RolesPermissionsPageProps {
	path?: string;
}

export const RolesPermissionsPage = (_props: RolesPermissionsPageProps) => {
	const { data: roles } = useRoles();
	const [selectedRoleId, setSelectedRoleId] = useState<number | undefined>(undefined);
	const [isCreateRoleOpen, setIsCreateRoleOpen] = useState(false);

	return (
		<div data-testid="roles-permissions-page">
			<TabHeader heading="Roles & Permissions" description="Manage roles and the permission matrix for your company." />

			<div className="mt-4">
				<InstructionsInfoBox
					data-testid="roles-permissions-instructions"
					title="How to use Roles & Permissions"
					instructions={ROLES_PERMISSIONS_INSTRUCTIONS}
					guideSteps={rolesPermissionsSteps}
					defaultExpanded
				/>
			</div>

			<div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
				<div>
					<div className="mb-2 flex items-center justify-between">
						<h3 className="text-sm font-semibold text-gray-700">Roles</h3>
						<Button
							btntype="primary"
							size="xs"
							onClick={() => setIsCreateRoleOpen(true)}
							className="flex items-center gap-1.5"
							data-testid="create-role-button"
						>
							<FiPlus size={14} />
							Create Role
						</Button>
					</div>
					<RolesList selectedRoleId={selectedRoleId} onSelectRole={setSelectedRoleId} />
				</div>

				{/* #5: min-w-0 lets the matrix's own overflow-auto contain the horizontal
				    scroll — without it a wide table stretches this grid track and the
				    WHOLE page (incl. the roles list) scrolls sideways. */}
				<div className="min-w-0">
					<h3 className="mb-2 text-sm font-semibold text-gray-700">Permission matrix</h3>
					<PermissionMatrixEditor
						roles={roles}
						highlightedRoleId={selectedRoleId}
						onClearSelection={() => setSelectedRoleId(undefined)}
					/>
				</div>
			</div>

			<CreateRoleModal
				isOpen={isCreateRoleOpen}
				onClose={() => setIsCreateRoleOpen(false)}
				onCreated={(role) => setSelectedRoleId(role.id)}
			/>
		</div>
	);
};
